import { UserInfo } from "@/types/auth";
import { 
  MenuItemConfig, 
  NavigationItem, 
  UserRole, 
  NavigationLevel,
  NavigationLevelType 
} from "@/types/menu";
import { MENU_CONFIG, navigationLevelCreators } from "./menu-config";
import { hasTenantPermissions, isSystemAdmin, isIntegratorAdmin, isOrganizationUser } from "./auth-utils";
import { 
  IconBuilding, 
  IconMap, 
  IconHome,
  IconMapPin,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconBuildingStore,
  IconTarget,
  IconBoxSeam
} from "@tabler/icons-react";
import { getDashboardIcon } from "./dashboard-icons";
import { getDashboardUrlFromDashboard } from "./dashboard-utils";

/**
 * Get icon for location type
 */
export const getLocationTypeIcon = (locationType: string) => {
  switch (locationType) {
    case 'site':
      return IconBuildingWarehouse;
    case 'facility':
      return IconBuildingSkyscraper;
    case 'venue':
      return IconBuildingStore;
    case 'zone':
      return IconTarget;
    case 'area':
      return IconBoxSeam;
    case 'place':
      return IconMapPin;
    case 'deployment':
      return IconTarget;
    case 'installation':
      return IconBuilding;
    case 'location':
    default:
      return IconHome;
  }
};

/**
 * Get user role from user info and delegation context
 * This function determines the effective role for menu display purposes
 */
export const getUserRole = (userInfo?: UserInfo, delegationContext?: any): UserRole | null => {
  if (!userInfo) return null;

  // If delegating, determine role based on delegation context
  if (delegationContext?.isDelegating && delegationContext?.currentDelegation) {
    const delegationType = delegationContext.currentDelegation.type;

    if (delegationType === 'integrator') {
      // When delegating to integrator, show integrator admin menu
      return "INTEGRATOR_ADMIN";
    } else if (delegationType === 'organization') {
      // When delegating to organization, show organization user menu
      return "ORGANIZATION_USER";
    }
  }

  // Determine role from entityType field
  if (userInfo.entityType === "system") {
    return "SYSTEM_ADMIN";
  } else if (userInfo.entityType === "integrator") {
    return "INTEGRATOR_ADMIN";
  } else if (userInfo.entityType === "organization") {
    return "ORGANIZATION_USER";
  }

  // Default fallback
  return "ORGANIZATION_USER";
};

/**
 * Filter menu items based on user role
 * Also removes section headers that have no visible items following them
 */
export const filterMenuByRole = (
  menu: MenuItemConfig[],
  userRole: UserRole | null
): MenuItemConfig[] => {
  if (!userRole) return [];

  // First pass: filter items by role
  const filteredItems = menu
    .filter(item => {
      // If no roles specified, item is visible to all
      if (!item.roles || item.roles.length === 0) return true;

      // Check if user role is in the allowed roles list
      return item.roles.some(role => {
        // Handle string comparison for UserRoleType values
        if (typeof role === 'string') {
          return role === userRole;
        }
        // Handle enum comparison for legacy AuthorityType values
        return role === userRole;
      });
    })
    .map(item => ({
      ...item,
      children: item.children ? filterMenuByRole(item.children, userRole) : undefined
    }));

  // Second pass: remove section headers that have no following items
  // A header is considered empty if no non-header items follow before the next header or end
  const result: MenuItemConfig[] = [];
  for (let i = 0; i < filteredItems.length; i++) {
    const item = filteredItems[i];

    if (item.type === 'header') {
      // Look ahead to see if there are any non-header items before the next header or end
      let hasVisibleItems = false;
      for (let j = i + 1; j < filteredItems.length; j++) {
        if (filteredItems[j].type === 'header') {
          break; // Hit next header, stop looking
        }
        // Found a non-header item, this section has content
        hasVisibleItems = true;
        break;
      }

      // Only include header if section has visible items
      if (hasVisibleItems) {
        result.push(item);
      }
    } else {
      result.push(item);
    }
  }

  return result;
};

/**
 * Replace template variables in URLs
 */
export const replaceUrlTemplates = (
  url: string, 
  variables: Record<string, string>
): string => {
  let result = url;
  Object.entries(variables).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });
  return result;
};

/**
 * Convert MenuItemConfig to NavigationItem
 */
export const convertToNavigationItem = (
  item: MenuItemConfig,
  variables: Record<string, string> = {}
): NavigationItem => {
  return {
    id: item.id,
    title: item.title,
    url: item.url ? replaceUrlTemplates(item.url, variables) : '#',
    icon: item.icon,
    description: item.description,
    children: item.children?.map(child => convertToNavigationItem(child, variables)),
    type: item.type === 'dynamic' ? 'link' : item.type,
    disabled: item.disabled
  };
};

/**
 * Resolve dynamic menu items
 */
export const resolveDynamicMenus = (
  menu: MenuItemConfig[],
  locations: any[] = [],
  dashboards: any[] = [],
  variables: Record<string, string> = {}
): NavigationItem[] => {
  const resolved = menu.map((item) => {
    if (item.type === 'dynamic' && item.id === 'dynamic-location-types') {
      // Create separate top-level menu items for each location type
      const dynamicItems: NavigationItem[] = [];
      
      if (locations.length > 0) {
        // Group locations by type
        const locationsByType: Record<string, any[]> = {};
        locations.forEach((location: any) => {
          const type = location.locationType || location.location_type || 'location';
          if (!locationsByType[type]) {
            locationsByType[type] = [];
          }
          locationsByType[type].push(location);
        });

        // Create a top-level menu item for each location type
        Object.entries(locationsByType).forEach(([type, typeLocations]) => {
          const typeDisplayName = type.charAt(0).toUpperCase() + type.slice(1) + 's';
          const typeSingular = type.charAt(0).toUpperCase() + type.slice(1);
          
          // Sort locations within type
          const sortedLocations = [...typeLocations].sort((a: any, b: any) =>
            a.name.localeCompare(b.name)
          );
          
          // Create the top-level menu item with children
          const typeIcon = getLocationTypeIcon(type);
          dynamicItems.push({
            id: `location-type-${type}`,
            title: typeDisplayName,
            url: "#",
            icon: typeIcon,
            children: [
              {
                id: `all-${type}`,
                title: `All ${typeDisplayName}`,
                url: `/location?type=${type}`,
                icon: typeIcon,
                description: `View and manage all ${type} locations`
              },
              {
                id: `${type}-map`,
                title: `${typeSingular} Map`,
                url: `/location/map?type=${type}`,
                icon: IconMap,
                description: `View ${type} locations on a map`
              },
              {
                id: `divider-${type}`,
                title: "",
                url: "",
                type: "divider"
              },
              // Add individual locations
              ...sortedLocations.map((location: any) => ({
                id: `location-${location.id}`,
                title: location.name,
                url: `/location/${location.id}`,
                icon: typeIcon
              }))
            ]
          });
        });
      }
      
      return dynamicItems;
    }
    
    if (item.type === 'dynamic' && item.id === 'dynamic-dashboards') {
      // Handle dynamic dashboards using cached data
      const dynamicItems: NavigationItem[] = [];

      if (dashboards.length > 0) {
        // Filter to only show dashboards that should appear in sidebar
        const visibleDashboards = dashboards.filter((dashboard: any) =>
          dashboard.showInSidebar !== false // Default to true if not specified
        );

        // Group dashboards by their group field
        const groupedDashboards: Record<string, any[]> = {};
        const ungroupedDashboards: any[] = [];

        visibleDashboards.forEach((dashboard: any) => {
          if (dashboard.group) {
            if (!groupedDashboards[dashboard.group]) {
              groupedDashboards[dashboard.group] = [];
            }
            groupedDashboards[dashboard.group].push(dashboard);
          } else {
            ungroupedDashboards.push(dashboard);
          }
        });

        // Sort groups alphabetically
        const sortedGroups = Object.keys(groupedDashboards).sort((a, b) =>
          a.localeCompare(b)
        );

        // Add grouped dashboards first (each group as a collapsible section)
        sortedGroups.forEach((groupName) => {
          const groupDashboards = groupedDashboards[groupName].sort((a: any, b: any) =>
            a.title.localeCompare(b.title)
          );

          dynamicItems.push({
            id: `dashboard-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`,
            title: groupName,
            url: '#',
            icon: getDashboardIcon('folder'), // Use folder icon for groups
            children: groupDashboards.map((dashboard: any) => ({
              id: `dashboard-${dashboard.id}`,
              title: dashboard.title,
              url: getDashboardUrlFromDashboard(dashboard),
              icon: getDashboardIcon(dashboard.icon)
            }))
          });
        });

        // Add ungrouped dashboards at the end (flat)
        const sortedUngrouped = [...ungroupedDashboards].sort((a: any, b: any) =>
          a.title.localeCompare(b.title)
        );

        sortedUngrouped.forEach((dashboard: any) => {
          dynamicItems.push({
            id: `dashboard-${dashboard.id}`,
            title: dashboard.title,
            url: getDashboardUrlFromDashboard(dashboard),
            icon: getDashboardIcon(dashboard.icon)
          });
        });
      }

      return dynamicItems;
    }
    
    // Handle children recursively
    let children: NavigationItem[] | undefined;
    if (item.children) {
      const resolvedChildren = resolveDynamicMenus(item.children, locations, dashboards, variables);
      children = resolvedChildren.flat(); // Flatten to handle dynamic items
    }
    
    return [convertToNavigationItem(item, variables)].map(navItem => ({
      ...navItem,
      children
    }));
  });
  
  return resolved.flat();
};

/**
 * Get menu items for a specific navigation level type
 */
export const getMenuForNavigationLevel = (
  levelType: string,
  userRole: UserRole | null,
  locations: any[] = [],
  dashboards: any[] = [],
  variables: Record<string, string> = {}
): NavigationItem[] => {
  // Check if this is a main navigation level type
  const mainMenu = filterMenuByRole(MENU_CONFIG.main, userRole);
  const mainMenuItem = mainMenu.find(item => item.navigationLevel === levelType);
  
  if (mainMenuItem?.children) {
    return resolveDynamicMenus(mainMenuItem.children, locations, dashboards, variables);
  }
  
  // Check contextual menus
  const contextualMenu = MENU_CONFIG.contextual[levelType];
  if (contextualMenu) {
    const filteredMenu = filterMenuByRole(contextualMenu, userRole);
    return filteredMenu.map(item => convertToNavigationItem(item, variables));
  }
  
  return [];
};

/**
 * Get main navigation menu
 */
export const getMainMenu = (userRole: UserRole | null, locations: any[] = [], dashboards: any[] = []): NavigationItem[] => {
  const filteredMenu = filterMenuByRole(MENU_CONFIG.main, userRole);
  return resolveDynamicMenus(filteredMenu, locations, dashboards);
};

/**
 * Get secondary navigation menu
 */
export const getSecondaryMenu = (userRole: UserRole | null): NavigationItem[] => {
  const filteredMenu = filterMenuByRole(MENU_CONFIG.secondary, userRole);
  return filteredMenu.map(item => convertToNavigationItem(item));
};

/**
 * Get user menu items
 */
export const getUserMenu = (userRole: UserRole | null): NavigationItem[] => {
  const filteredMenu = filterMenuByRole(MENU_CONFIG.userMenu, userRole);
  return filteredMenu.map(item => convertToNavigationItem(item));
};

/**
 * Find menu item by ID across all menus
 */
export const findMenuItemById = (
  id: string, 
  userRole: UserRole | null
): MenuItemConfig | null => {
  const allMenus = [
    ...MENU_CONFIG.main,
    ...MENU_CONFIG.secondary,
    ...MENU_CONFIG.userMenu,
    ...Object.values(MENU_CONFIG.contextual).flat()
  ];
  
  const findInMenu = (items: MenuItemConfig[]): MenuItemConfig | null => {
    for (const item of items) {
      if (item.id === id) {
        // Check if user has access to this item
        if (!item.roles || !userRole || item.roles.includes(userRole)) {
          return item;
        }
      }
      if (item.children) {
        const found = findInMenu(item.children);
        if (found) return found;
      }
    }
    return null;
  };
  
  return findInMenu(allMenus);
};

/**
 * Get navigation level from path - uses the config-driven approach
 */
export const getNavigationLevelFromPath = (pathname: string): NavigationLevel | null => {
  const levelInfo = navigationLevelCreators.getNavigationLevelFromPath(pathname);
  if (!levelInfo) return null;
  
  return navigationLevelCreators.createNavigationLevel(levelInfo.type, levelInfo.data);
};

/**
 * Create navigation level - uses the config-driven approach
 */
export const createNavigationLevel = (
  type: string,
  data?: any
): NavigationLevel | null => {
  return navigationLevelCreators.createNavigationLevel(type, data);
};